/**
 * A studio draft's links as `ComposedLink`s, the shape compose reads and the
 * compose route writes, plus the few problems only the draft's own shape can
 * have. Everything else about a link (records, fields, choices, picks, kinds)
 * is `composeProblems`' to judge, so it is judged once.
 */
import type { ComposedLink, ComposedMapping } from '../compose';
import { COMPOSED_TEMPLATE_ID } from '../compose';
import { carryMappingOf, draftFormulaSpec } from '../compose-author';
import { opIsNumeric, opNeedsValue } from '../links';
import type { StudioDraft } from './draft';
import type { StudioProblem } from './judge';

export const authorStudioLinks = (draft: StudioDraft): { links: ComposedLink[]; problems: StudioProblem[] } => {
  const problems: StudioProblem[] = [];
  const links = draft.links.map((link, index): ComposedLink => {
    const path = `${COMPOSED_TEMPLATE_ID}.links[${index}]`;
    const part = `link:${link.key}`;
    const say = (code: string, message: string): void => {
      problems.push({ code, message, path, part });
    };

    const all: ComposedLink['trigger']['all'] = [];
    for (const group of link.when) {
      // An empty group is dropped from the composed link, so the reader can
      // still judge everything else; the problem is said here instead.
      if (group.any.length === 0) {
        say('LINK_GROUP_EMPTY', 'A group of conditions holds none. Give it a condition or take the group away.');
        continue;
      }
      for (const condition of group.any) {
        const count = condition.values.length;
        if (opNeedsValue(condition.op) && count === 0) {
          say('LINK_CONDITION_VALUES', `The condition on "${condition.field}" (${condition.op}) needs a value.`);
        } else if (!opNeedsValue(condition.op) && count > 0) {
          say('LINK_CONDITION_VALUES', `The condition on "${condition.field}" (${condition.op}) takes no value.`);
        } else if (opIsNumeric(condition.op) && (count !== 1 || typeof condition.values[0] !== 'number')) {
          say('LINK_CONDITION_VALUES', `The condition on "${condition.field}" (${condition.op}) takes exactly one number.`);
        }
      }
      all.push({
        any: group.any.map((condition) => ({
          fieldKey: condition.field,
          op: condition.op,
          ...(condition.values.length === 0
            ? {}
            : { value: condition.values.length === 1 ? condition.values[0] : condition.values }),
          ...(condition.via === undefined ? {} : { via: condition.via }),
        })),
      });
    }

    const fieldMap: Record<string, ComposedMapping> = {};
    const fromFields = draft.records.find((record) => record.key === link.from)?.fields ?? [];
    for (const fill of link.fills) {
      if (fill.to in fieldMap) {
        say('LINK_FILL_TWICE', `"${fill.to}" is filled twice by one link. Keep one of the two.`);
        continue;
      }
      fieldMap[fill.to] =
        fill.kind === 'field'
          ? carryMappingOf(fromFields.find((field) => field.key === fill.field), fill.field)
          : fill.kind === 'const'
            ? { kind: 'const', value: fill.value }
            : fill.kind === 'reference'
              ? { kind: 'reference', via: fill.via, fieldKey: fill.field }
              : { kind: 'formula', spec: draftFormulaSpec(fill.formula) };
    }

    return {
      fromRecordKey: link.from,
      toRecordKey: link.to,
      pattern: link.pattern,
      sign: link.sign,
      mode: link.mode,
      trigger: { all },
      fieldMap,
    };
  });
  return { links, problems };
};
